import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, CheckCircle2, XCircle, Info } from 'lucide-react';
import Button from '../../components/ui/Button';
import { authService } from '../../services';
import useEmailCheck from '../../hooks/useEmailCheck';
import logoFull from '../../assets/images/logo_full.png';
import logoIcon from '../../assets/images/logo_icon.png';

/**
 * Registration Page - Multi-step registration
 *
 * Onboarding model: instead of an upfront modal slideshow that's dismissed and
 * forgotten, guidance lives in a persistent context panel (the left split-screen
 * column on desktop, a collapsible banner on mobile) that updates per step. This
 * keeps "what am I doing / what's next" visible the whole time.
 *
 * Step 0: Role Selection (client vs independent trainer)
 *
 * Client Flow:   Step 1 Org → Step 2 Account → Step 3 Plan → Step 4 Success
 * Trainer Flow:  Step 1 Account → Step 2 Plan → Step 3 Success
 */

// Lightweight, no-op-safe funnel tracking. Routes to GTM (dataLayer) or gtag
// when present; silently does nothing if no analytics is loaded. Analytics
// must never break the signup flow, hence the swallow.
const trackSignupEvent = (name, props = {}) => {
  try {
    if (typeof window === 'undefined') return;
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...props });
    if (typeof window.gtag === 'function') window.gtag('event', name, props);
  } catch {
    /* ignore analytics errors */
  }
};

// Per-step guidance copy. Rendered as a detail card in the form column (all
// screen sizes). 1-indexed to match currentStep. `features` = what you'll do
// on this step; `responsibilities` = the broader role (optional, shown as a grid).
const GUIDANCE = {
  client: {
    1: {
      eyebrow: 'About your organization',
      heading: 'Organization details',
      body: 'Tell us about your organization. This helps us tailor the platform and brand the participant screens, reports and certificates your trainees see.',
      features: ['Enter your organization name', 'Add optional contact & address details'],
      next: 'Up next: create your client login',
    },
    2: {
      eyebrow: 'Your account details',
      heading: 'Create your client account',
      body: "Create your client account. As the primary client, you'll manage your organization's entire training ecosystem.",
      features: ['Set up your login credentials', 'Add your contact information'],
      responsibilities: ['Add and manage trainers', 'Create and schedule sessions', 'Monitor reports and performance', 'Manage subscription and billing'],
      next: 'Up next: choose a plan',
    },
    3: {
      eyebrow: 'Choose your plan',
      heading: 'Pick the plan that fits',
      body: 'Select a plan that fits your training needs. Start with a free trial and upgrade anytime — every plan includes the full activity toolkit.',
      features: ['Compare plan features', 'Secure payment processing'],
      next: "Up next: you're all set",
    },
  },
  trainer: {
    1: {
      eyebrow: 'Your trainer account',
      heading: 'Your personal workspace',
      body: 'Enter your details and create your trainer account — your personal workspace to manage everything. No organization setup needed.',
      features: ['Name, email and password', 'Phone number (optional)'],
      responsibilities: ['Create training sessions', 'Run live activities', 'View participant reports', 'Track engagement scores'],
      next: 'Up next: choose a plan',
    },
    2: {
      eyebrow: 'Choose your plan',
      heading: 'Pick the plan that fits',
      body: 'Pick a plan that matches your training volume. Start free and upgrade whenever you need more.',
      features: ['Free: 3 sessions, 30 participants each', 'Pro: 20 sessions/month with full reports'],
      next: "Up next: you're all set",
    },
  },
};

const CLIENT_STEPS = ['Organization', 'Your details', 'Plan'];
const TRAINER_STEPS = ['Your details', 'Plan'];

// Next-action checklist shown on the success screen so users leave with momentum.
const NEXT_STEPS = {
  client: ['Add your first trainer', 'Create and schedule a session', 'Share the join code with participants'],
  trainer: ['Create your first session', 'Add activities — polls, quizzes, feedback', 'Share the join code with participants'],
};

// Accessible hover/focus tooltip used to explain plan limits in-context.
const InfoTip = ({ label, children }) => (
  <span className="relative inline-flex group align-middle">
    <button
      type="button"
      aria-label={label}
      className="inline-flex text-neutral-400 hover:text-primary-600 focus:text-primary-600 focus:outline-none"
    >
      <Info size={13} />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-56 -translate-x-1/2 rounded-lg bg-neutral-900 px-3 py-2 text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
    >
      {children}
    </span>
  </span>
);

// Per-field speech-bubble shown on hover/focus — DESKTOP ONLY (lg+). The parent
// must be `relative group` (typically the input wrapper) so the bubble anchors
// above the input and reveals on group-hover or group-focus-within (keyboard).
// On mobile there is no hover, so guidance falls back to <FieldInlineHelp/>.
const FieldTip = ({ text }) => (
  <span
    role="tooltip"
    className="pointer-events-none absolute bottom-full left-0 z-30 mb-2 hidden w-full rounded-xl bg-neutral-900 px-3 py-2 text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 lg:block group-hover:opacity-100 group-focus-within:opacity-100"
  >
    {text}
    {/* downward tail */}
    <span className="absolute left-6 top-full h-0 w-0 border-x-[7px] border-t-[7px] border-x-transparent border-t-neutral-900" />
  </span>
);

// Mobile/tablet fallback for FieldTip — always-visible helper text, hidden on lg+
// where the hover bubble takes over. Keeps field guidance reachable on touch.
const FieldInlineHelp = ({ text }) => (
  <p className="lg:hidden mt-1 text-xs text-neutral-500">{text}</p>
);

// Labeled, accessible progress stepper (replaces the unlabeled dot indicators).
const LabeledStepper = ({ steps, current }) => (
  <nav aria-label="Progress" className="mb-6">
    <ol className="flex items-start">
      {steps.map((label, index) => {
        const stepNum = index + 1;
        const isDone = current > stepNum;
        const isActive = current === stepNum;
        const isLast = index === steps.length - 1;
        return (
          <li key={label} className={`flex items-start ${isLast ? '' : 'flex-1'}`}>
            <div className="flex flex-col items-center">
              <div
                aria-current={isActive ? 'step' : undefined}
                className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                  isDone
                    ? 'bg-primary-600 text-white'
                    : isActive
                    ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                    : 'bg-neutral-200 text-neutral-500'
                }`}
              >
                {isDone ? (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  stepNum
                )}
              </div>
              <span
                className={`mt-1.5 max-w-[6rem] text-center text-xs font-medium leading-tight ${
                  isActive ? 'text-primary-600' : isDone ? 'text-neutral-600' : 'text-neutral-400'
                }`}
              >
                {label}
              </span>
            </div>
            {!isLast && (
              <div className={`mt-[18px] h-0.5 flex-1 transition-all ${isDone ? 'bg-primary-600' : 'bg-neutral-200'}`} />
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

// Dark-styled per-step detail card for the colored LEFT panel. Carries the
// modal-level detail: what you'll do on this step + (optional) the broader role
// responsibilities + what's next.
const StepDetail = ({ role, currentStep }) => {
  const steps = role === 'trainer' ? TRAINER_STEPS : CLIENT_STEPS;
  const guide = GUIDANCE[role]?.[currentStep];
  if (!guide) return null;

  const CheckDot = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );

  return (
    <div className="rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-primary-200">
        Step {currentStep} of {steps.length} · {guide.eyebrow}
      </p>
      <p className="mb-3 text-sm leading-relaxed text-primary-50">{guide.body}</p>

      {/* What you'll do on this step */}
      <ul className="space-y-1.5">
        {guide.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-primary-50">
            <CheckDot className="mt-0.5 h-4 w-4 flex-shrink-0 text-white" />
            {f}
          </li>
        ))}
      </ul>

      {/* Your responsibilities (only on steps where it applies) */}
      {guide.responsibilities && (
        <div className="mt-4 border-t border-white/15 pt-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-200">
            Your responsibilities
          </p>
          <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
            {guide.responsibilities.map((r) => (
              <div key={r} className="flex items-start gap-2 text-xs text-primary-100">
                <CheckDot className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-white" />
                {r}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What's next */}
      {guide.next && (
        <p className="mt-3 border-t border-white/15 pt-3 text-xs font-medium text-primary-200">
          {guide.next}
        </p>
      )}
    </div>
  );
};

// Left colored panel content. Welcome intro shows on step 1; the per-step
// detail card shows on every step (this is what the user marked on the left).
const WelcomeAside = ({ role, currentStep, isSuccess }) => {
  if (isSuccess) {
    return (
      <div className="max-w-md text-white">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
          <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="mb-3 text-3xl font-bold">You're all set!</h2>
        <p className="text-primary-100">
          Your account is ready. Sign in to open your dashboard and{' '}
          {role === 'trainer' ? 'create your first session.' : 'invite your team.'}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md text-white">
      {currentStep === 1 && (
        <>
          <p className="mb-2 text-sm font-medium uppercase tracking-wide text-primary-200">
            {role === 'trainer' ? 'Independent trainer setup' : 'Organization setup'}
          </p>
          <h2 className="mb-3 text-3xl font-bold leading-tight">Welcome to Aktivity</h2>
          <p className="mb-5 text-sm leading-relaxed text-primary-100">
            Get started with our interactive training platform. Complete your registration in a few
            simple steps to unlock powerful training tools.
          </p>
          <ul className="mb-6 space-y-2">
            {[
              'Quick 5-minute setup process',
              'No credit card required for trial',
              'Cancel or upgrade anytime',
            ].map((item) => (
              <li
                key={item}
                className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-2.5 text-sm text-primary-50 backdrop-blur-sm"
              >
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
                  <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </>
      )}
      <StepDetail role={role} currentStep={currentStep} />
    </div>
  );
};

// Participant join CTA — shown in the left column on the role-selection screen.
const ParticipantJoinAside = ({ sessionCode, setSessionCode, onSubmit }) => (
  <div className="max-w-md">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <div>
        <h1 className="text-2xl font-bold text-white">Joining as a participant?</h1>
        <p className="text-primary-200 text-sm">Enter your session code to join</p>
      </div>
    </div>

    <p className="text-primary-100 text-base mb-8">
      Enter the session code provided by your trainer to participate in the interactive training session.
    </p>

    <form onSubmit={onSubmit} className="space-y-4">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <span className="text-neutral-400 font-medium">#</span>
        </div>
        <input
          type="text"
          value={sessionCode}
          onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
          placeholder="Enter code here"
          className="w-full pl-10 pr-4 py-4 bg-white border-2 border-white rounded-xl focus:outline-none focus:ring-4 focus:ring-white/30 text-neutral-900 placeholder-neutral-400 font-medium text-lg tracking-wider uppercase"
          maxLength={10}
        />
      </div>
      <button
        type="submit"
        disabled={!sessionCode.trim()}
        className="w-full py-4 bg-black rounded-xl font-bold text-lg hover:bg-neutral-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
      >
        <span className="text-white">Join Session</span>
        <svg className="w-5 h-5 text-white" fill="none" stroke="white" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </button>
    </form>

    <p className="text-primary-200 text-sm mt-6">
      Don't have a code? Ask your trainer or session organizer for the session code.
    </p>
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState(null); // 'client' or 'trainer'
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionCode, setSessionCode] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // Organization fields
    organizationName: '',
    website: '',
    description: '',
    // Address fields
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    // Client fields
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    password: '',
    confirmPassword: '',
    // Plan selection
    plan: 'free',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Email availability check
  const emailCheck = useEmailCheck(authService.checkEmail);

  // Determine the success step for current role
  const isSuccessStep = selectedRole === 'trainer' ? currentStep === 3 : currentStep === 4;

  // Check for payment success on return from Dodo checkout
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setPaymentSuccess(true);
      // Set to success step based on role
      const savedRole = localStorage.getItem('pendingRole');
      if (savedRole) {
        setSelectedRole(savedRole);
      }
      setCurrentStep(savedRole === 'trainer' ? 3 : 4);
      trackSignupEvent('signup_payment_success', { role: savedRole || 'client' });
      // Clear pending plan from localStorage
      localStorage.removeItem('pendingPlan');
      localStorage.removeItem('pendingRole');
    }
  }, [searchParams]);

  // Read plan from URL parameter or localStorage (for page refresh persistence)
  useEffect(() => {
    const validPlans = ['free', 'pro', 'pro_plus', 'enterprise_starter'];

    // First check URL parameter
    const planFromUrl = searchParams.get('plan');
    if (planFromUrl && validPlans.includes(planFromUrl)) {
      setFormData(prev => ({ ...prev, plan: planFromUrl }));
      localStorage.setItem('pendingPlan', planFromUrl);
      return;
    }

    // Fallback to localStorage if no URL param
    const savedPlan = localStorage.getItem('pendingPlan');
    if (savedPlan && validPlans.includes(savedPlan)) {
      setFormData(prev => ({ ...prev, plan: savedPlan }));
    }
  }, [searchParams]);

  // Funnel: record each step view (skip the success step).
  useEffect(() => {
    if (selectedRole && !isSuccessStep) {
      trackSignupEvent('signup_step_viewed', { role: selectedRole, step: currentStep });
    }
  }, [selectedRole, currentStep, isSuccessStep]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
      emailCheck.checkEmail(value);
    }
    // Real-time password validation
    if (name === 'password') {
      if (value.length > 0 && value.length < 8) {
        setErrors((prev) => ({ ...prev, password: 'Password must be at least 8 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, password: '' }));
      }
      // Also validate confirmPassword match if it's already filled
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else if (formData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }
    if (name === 'confirmPassword') {
      if (value && value !== formData.password) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const handleSessionCodeSubmit = (e) => {
    e.preventDefault();
    if (sessionCode.trim()) {
      navigate(`/join/${sessionCode.trim().toUpperCase()}`);
    }
  };

  // Handle role selection — go straight into the form; guidance now lives in the
  // persistent context panel instead of an upfront modal.
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setCurrentStep(1);
    setFormData((prev) => ({ ...prev, plan: 'free' }));
    setErrors({});
    trackSignupEvent('signup_role_selected', { role });
  };

  // Go back to role selection
  const handleBackToRoleSelection = () => {
    setSelectedRole(null);
    setCurrentStep(1);
    setErrors({});
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    } else if (emailCheck.available === false) {
      newErrors.email = 'This email is already in use';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Trainer flow personal details validation (phone optional)
  const validateTrainerStep1 = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    } else if (emailCheck.available === false) {
      newErrors.email = 'This email is already in use';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (selectedRole === 'trainer') {
      // Trainer flow: Step 1 (personal details) -> Step 2 (plan)
      if (currentStep === 1 && validateTrainerStep1()) {
        setCurrentStep(2);
      }
    } else {
      // Client flow: Step 1 (org) -> Step 2 (details) -> Step 3 (plan)
      if (currentStep === 1 && validateStep1()) {
        setCurrentStep(2);
      } else if (currentStep === 2 && validateStep2()) {
        setCurrentStep(3);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      handleBackToRoleSelection();
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePlanSelect = (plan) => {
    setFormData((prev) => ({ ...prev, plan }));
    // Persist to localStorage so it survives page refresh
    localStorage.setItem('pendingPlan', plan);
    trackSignupEvent('signup_plan_selected', { role: selectedRole, plan });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    trackSignupEvent('signup_submitted', { role: selectedRole, plan: formData.plan });

    try {
      let registrationData;

      if (selectedRole === 'trainer') {
        // Solo trainer registration
        registrationData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          role: 'trainer',
          plan: formData.plan,
          phone: formData.phone || undefined,
        };
      } else {
        // Client/org registration (existing flow)
        registrationData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          role: 'client',
          organizationName: formData.organizationName,
          jobTitle: formData.jobTitle || undefined,
          website: formData.website || undefined,
          description: formData.description || undefined,
          plan: formData.plan,
          // Address fields
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zipCode || undefined,
          country: formData.country || undefined,
        };
      }

      const response = await authService.register(registrationData);

      // NOTE: do NOT clear localStorage 'token'/'user' here. authService.register
      // (free-plan path) just persisted them via tokenStorage.setToken/setUser;
      // removing them here logs the user straight out and forces a manual
      // login after a successful signup. Paid-plan flow doesn't set them, so
      // there's nothing to clear in that path either.

      // If paid plan, redirect to checkout
      if (response.checkoutUrl) {
        // Save role so we can restore it on payment return
        localStorage.setItem('pendingRole', selectedRole);
        trackSignupEvent('signup_payment_redirect', { role: selectedRole, plan: formData.plan });
        window.location.href = response.checkoutUrl;
        return;
      }

      // For free plan, show success
      trackSignupEvent('signup_success', { role: selectedRole, plan: formData.plan });
      if (selectedRole === 'trainer') {
        setCurrentStep(3); // Trainer success is step 3
      } else {
        setCurrentStep(4); // Client success is step 4
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ submit: error.message || 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Input class helper
  const inputClass = "w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white transition-all text-neutral-900 placeholder-neutral-400";

  // Shared plan card component
  const PlanCard = ({ planKey, name, priceInr, priceUsd, period, description, features, badge }) => (
    <div
      onClick={() => handlePlanSelect(planKey)}
      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
        formData.plan === planKey
          ? 'border-primary-500 bg-primary-50'
          : 'border-neutral-200 hover:border-neutral-300 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
          formData.plan === planKey ? 'border-primary-500 bg-primary-500' : 'border-neutral-300'
        }`}>
          {formData.plan === planKey && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-neutral-900">{name}</h3>
              {badge && (
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                  {badge}
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm sm:text-lg font-bold text-neutral-900">
                {priceInr} {priceUsd && <span className="text-sm font-normal text-neutral-500">/ {priceUsd}</span>}
                {planKey !== 'free' && <span className="text-xs font-normal text-neutral-400"> +GST</span>}
              </p>
              <p className="text-xs text-neutral-500">{period}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-600 mb-2">{description}</p>
          <ul className="space-y-1">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-1.5 text-xs text-neutral-600">
                <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  // Explainer shown above the plan cards so the limits are understandable.
  const PlanLimitsExplainer = ({ includeTrainerSeats }) => (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
      <p className="text-xs leading-relaxed text-neutral-600">
        Every plan includes the full activity toolkit — polls, quizzes, word clouds, Q&amp;A, feedback and more.
        Plans differ by how many{' '}
        <span className="font-medium text-neutral-700">sessions</span>
        <InfoTip label="What is a session?">
          A session is one training event you run, with its own join code, activities and report.
        </InfoTip>{' '}
        you can run and how many{' '}
        <span className="font-medium text-neutral-700">participants</span>
        <InfoTip label="Participants per session">
          The maximum number of people who can join a single session at the same time.
        </InfoTip>{' '}
        can join each one
        {includeTrainerSeats ? (
          <>
            , plus how many{' '}
            <span className="font-medium text-neutral-700">trainer seats</span>
            <InfoTip label="Trainer seats">
              How many trainer accounts your organization can have running sessions.
            </InfoTip>{' '}
            you get.
          </>
        ) : (
          '.'
        )}
      </p>
    </div>
  );

  // Role selection screen (Step 0)
  const RoleSelection = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-2">Get started with Aktivity</h2>
        <p className="text-xs sm:text-sm text-neutral-500">How would you like to use Aktivity?</p>
      </div>

      <div className="space-y-4">
        {/* Organization / Client Card */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={() => handleRoleSelect('client')}
          className="relative p-5 rounded-xl border-2 cursor-pointer transition-all border-neutral-200 hover:border-primary-300 hover:shadow-sm bg-white"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-primary-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-neutral-900 text-sm sm:text-lg mb-1">I'm setting up training for my organization</h3>
              <p className="text-xs sm:text-sm text-neutral-500 leading-relaxed">
                Register your company or team. Add trainers, manage sessions, and track training across your organization.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {['Multi-trainer support', 'Organization dashboard', 'All plan tiers'].map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-md border border-primary-100">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 mt-1">
              <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </motion.div>

        {/* Independent Trainer Card */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={() => handleRoleSelect('trainer')}
          className="relative p-5 rounded-xl border-2 cursor-pointer transition-all border-neutral-200 hover:border-emerald-300 hover:shadow-sm bg-white"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-neutral-900 text-sm sm:text-lg mb-1">I'm an independent trainer</h3>
              <p className="text-xs sm:text-sm text-neutral-500 leading-relaxed">
                Quick setup for solo trainers. Start creating and running interactive training sessions in minutes.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {['Quick 2-step setup', 'Personal workspace', 'Free & Pro plans'].map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-md border border-emerald-100">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 mt-1">
              <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile-only participant join hint (left panel is hidden on small screens) */}
      <div className="lg:hidden rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-center">
        <p className="text-xs text-neutral-500">
          Joining a session as a participant?{' '}
          <Link to="/join" className="font-semibold text-primary-600 hover:text-primary-700">
            Enter your code
          </Link>
        </p>
      </div>
    </div>
  );

  // Trainer personal details form (Step 1 of trainer flow)
  const TrainerPersonalDetailsStep = () => (
    <div className="space-y-4">
      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              className={inputClass}
            />
            <FieldTip text="Your name as shown to participants on the session screen and on reports." />
          </div>
          {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>}
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">
            Last Name <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              className={inputClass}
            />
            <FieldTip text="Your surname as shown to participants and on reports." />
          </div>
          {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>}
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
          Email <span className="text-red-500">*</span>
        </label>
        <div className="relative group">
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            className={`${inputClass} pr-10`}
          />
          {formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {emailCheck.checking && <Loader2 size={18} className="text-neutral-400 animate-spin" />}
              {!emailCheck.checking && emailCheck.available === true && <CheckCircle2 size={18} className="text-green-500" />}
              {!emailCheck.checking && emailCheck.available === false && <XCircle size={18} className="text-red-500" />}
            </span>
          )}
          <FieldTip text="You'll use this email to sign in." />
        </div>
        {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
        {!errors.email && emailCheck.error && <p className="text-sm text-red-600 mt-1">{emailCheck.error}</p>}
        {!errors.email && !emailCheck.error && emailCheck.available === true && (
          <p className="text-sm text-green-600 mt-1">Email is available</p>
        )}
        {!errors.email && !emailCheck.error && emailCheck.available !== true && (
          <FieldInlineHelp text="You'll use this email to sign in." />
        )}
      </div>

      {/* Phone (optional for trainers) */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-2">
          Phone Number <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="relative group">
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            className={inputClass}
          />
          <FieldTip text="Optional — used for account security and important updates." />
        </div>
        <FieldInlineHelp text="Optional — used for account security and important updates." />
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative group">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a password"
            className={`${inputClass} pr-11`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          <FieldTip text="At least 8 characters. Mix letters and numbers for a stronger password." />
        </div>
        {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password}</p>}
        {!errors.password && <FieldInlineHelp text="Must be at least 8 characters. Mix letters and numbers." />}
      </div>

      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-2">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="relative group">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            className={`${inputClass} pr-11`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
            tabIndex={-1}
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          <FieldTip text="Re-type your password exactly to confirm it." />
        </div>
        {errors.confirmPassword && <p className="text-sm text-red-600 mt-1">{errors.confirmPassword}</p>}
        {!errors.confirmPassword && <FieldInlineHelp text="Re-type your password exactly to confirm it." />}
      </div>

      {/* Buttons */}
      <div className="flex gap-3 mt-4">
        <button
          type="button"
          onClick={handleBack}
          className="flex-1 px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
        >
          Back
        </button>
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={handleNext}
          className="flex-1"
        >
          <span className="flex items-center justify-center gap-2">
            Continue
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </span>
        </Button>
      </div>
    </div>
  );

  // Trainer plan selection (Free & Pro only)
  const TrainerPlanStep = () => {
    const billingNote = formData.plan !== 'free' ? 'Billed monthly · GST applies · cancel anytime' : null;
    return (
      <div className="space-y-4">
        <PlanLimitsExplainer includeTrainerSeats={false} />

        <div className="space-y-3">
          <PlanCard
            planKey="free"
            name="Free Trial"
            priceInr="₹0"
            priceUsd="$0"
            period="forever"
            description="Experience Aktivity in a real training session"
            features={['3 sessions total (one-time)', 'Up to 30 participants/session', 'All core features', 'Basic report']}
          />
          <PlanCard
            planKey="pro"
            name="Pro"
            priceInr="₹1,999"
            priceUsd="$24"
            period="/month"
            description="For trainers who want proof of impact"
            features={['20 sessions/month', '30 participants/session', '1 trainer access', 'Full reports']}
          />
        </div>

        {/* Reassurance */}
        <p className="text-center text-xs text-neutral-500">
          {formData.plan === 'free'
            ? 'Free needs no credit card — upgrade anytime as you grow.'
            : 'No commitment — you can change or cancel your plan anytime.'}
        </p>

        {/* Payment Info for Paid Plans */}
        {formData.plan !== 'free' && (
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-blue-800">
                  After registration, you'll be redirected to our secure payment partner to complete your subscription.
                </p>
                {billingNote && <p className="mt-1 text-xs font-medium text-blue-700">{billingNote}</p>}
                <div className="flex items-center gap-2 mt-2 text-xs text-blue-700">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Secure payment powered by Dodo Payments</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Terms */}
        <p className="text-xs text-neutral-500">
          By signing up, you agree to our{' '}
          <a href="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
            Privacy Policy
          </a>
        </p>

        {/* Error Message */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {errors.submit}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
          >
            Back
          </button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            className="flex-1"
          >
            <span className="flex items-center justify-center gap-2 text-sm sm:text-base">
              {loading ? 'Creating Account...' : formData.plan === 'free' ? 'Complete Sign Up' : (
                <>
                  <span className="sm:hidden">Pay Now</span>
                  <span className="hidden sm:inline">Continue to Payment</span>
                </>
              )}
              {!loading && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={formData.plan === 'free' ? "M5 13l4 4L19 7" : "M17 8l4 4m0 0l-4 4m4-4H3"} />
                </svg>
              )}
            </span>
          </Button>
        </div>
      </div>
    );
  };

  // Success state content (shared between flows)
  const SuccessContent = () => {
    const nextSteps = NEXT_STEPS[selectedRole] || NEXT_STEPS.client;
    return (
      <div className="text-center py-8">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-3">
          {paymentSuccess ? 'Payment Successful!' : 'Registration Successful!'}
        </h2>
        <p className="text-xs sm:text-sm text-neutral-500 mb-8">
          {paymentSuccess ? (
            <>
              Your payment has been processed successfully.<br />
              Your subscription is now active. Please sign in to access your dashboard.
            </>
          ) : (
            <>
              Your account has been created successfully.<br />
              Please sign in to access your dashboard.
            </>
          )}
        </p>

        {/* Organization Summary - Only show for client flow if we have the data */}
        {selectedRole === 'client' && formData.organizationName && !paymentSuccess && (
          <div className="bg-neutral-50 rounded-xl p-5 mb-6 text-left border border-neutral-200">
            <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Organization</p>
            <p className="font-semibold text-neutral-900 text-sm sm:text-lg">{formData.organizationName}</p>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1">{formData.email}</p>
            <div className="mt-3 pt-3 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Plan</p>
              <p className="font-medium text-neutral-900">
                {formData.plan === 'free' && 'Free Plan'}
                {formData.plan === 'pro' && 'Pro Plan'}
                {formData.plan === 'pro_plus' && 'Pro Plus Plan'}
                {formData.plan === 'enterprise_starter' && 'Enterprise Starter Plan'}
              </p>
            </div>
          </div>
        )}

        {/* Trainer Summary */}
        {selectedRole === 'trainer' && !paymentSuccess && (
          <div className="bg-neutral-50 rounded-xl p-5 mb-6 text-left border border-neutral-200">
            <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Account</p>
            <p className="font-semibold text-neutral-900 text-sm sm:text-lg">{formData.firstName} {formData.lastName}</p>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1">{formData.email}</p>
            <div className="mt-3 pt-3 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Plan</p>
              <p className="font-medium text-neutral-900">
                {formData.plan === 'free' && 'Free Plan'}
                {formData.plan === 'pro' && 'Pro Plan'}
              </p>
            </div>
          </div>
        )}

        {/* Payment Success Summary */}
        {paymentSuccess && (
          <div className="bg-green-50 rounded-xl p-5 mb-6 text-left border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-green-800">Your subscription is active</p>
                <p className="text-sm text-green-600">Your plan will be activated within a few moments</p>
              </div>
            </div>
            <p className="text-xs text-green-700">
              A confirmation email has been sent to your registered email address.
            </p>
          </div>
        )}

        {/* What's next — gives the user a clear first action instead of a dead end */}
        <div className="bg-primary-50 rounded-xl p-5 mb-8 text-left border border-primary-100">
          <p className="text-sm font-semibold text-primary-900 mb-3">After you sign in, here's what's next:</p>
          <ol className="space-y-2">
            {nextSteps.map((step, idx) => (
              <li key={step} className="flex items-start gap-3 text-sm text-primary-900/90">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
                  {idx + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => navigate('/login')}
        >
          <span className="flex items-center justify-center gap-2">
            Go to Login
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </span>
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Left Side - Fixed Colored Panel (context-aware) */}
      <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-10 flex-col justify-between fixed top-0 left-0 h-screen overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-500/20 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative z-10 flex flex-col h-full justify-between">
          {/* Logo */}
          <Link to="/login" className="flex items-center gap-1 group">
            <img src={logoIcon} alt="Aktivity" className="h-9 w-9 object-contain group-hover:scale-105 transition-transform" />
            <img src={logoFull} alt="Aktivity" className="h-9 object-contain group-hover:scale-105 transition-transform" />
          </Link>

          {/* Context: guidance once a role is chosen, otherwise the participant-join CTA */}
          {selectedRole ? (
            <WelcomeAside role={selectedRole} currentStep={currentStep} isSuccess={isSuccessStep} />
          ) : (
            <ParticipantJoinAside
              sessionCode={sessionCode}
              setSessionCode={setSessionCode}
              onSubmit={handleSessionCodeSubmit}
            />
          )}

          {/* Footer */}
          <div className="text-primary-200 text-sm">
            Powered by Aktivity - Interactive Training Platform
          </div>
        </div>
      </div>

      {/* Right Side - Registration Form (Scrollable) */}
      <div className="w-full lg:w-[55%] lg:ml-[45%] min-h-screen flex flex-col bg-white">
        {/* Top Navigation */}
        <div className="flex justify-between items-start p-5 lg:p-8">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center flex-shrink-0">
            <img src={logoIcon} alt="Logo" className="h-7 w-7 object-contain" />
            <img src={logoFull} alt="Aktivity" className="h-7 object-contain ml-1" />
          </Link>
          <div className="hidden lg:block"></div>

          <p className="text-xs sm:text-sm text-neutral-600 text-right flex-shrink-0 ml-4">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold whitespace-nowrap">
              Sign in
            </Link>
          </p>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-16 pb-12 overflow-y-auto">
          <div className="w-full max-w-md">
            {/* Success State */}
            {isSuccessStep ? (
              <SuccessContent />
            ) : !selectedRole ? (
              /* Step 0: Role Selection */
              <RoleSelection />
            ) : selectedRole === 'trainer' ? (
              /* Trainer Flow */
              <>
                {/* Form Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                      Independent Trainer
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-2">Create your account</h2>
                  <p className="text-xs sm:text-sm text-neutral-500">
                    {currentStep === 1 && 'Enter your personal details'}
                    {currentStep === 2 && 'Choose the plan that fits your needs'}
                  </p>
                </div>

                {/* Labeled Step Indicator */}
                <LabeledStepper steps={TRAINER_STEPS} current={currentStep} />

                {/* Trainer Registration Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {currentStep === 1 && TrainerPersonalDetailsStep()}
                  {currentStep === 2 && TrainerPlanStep()}
                </form>
              </>
            ) : (
              /* Client Flow */
              <>
                {/* Form Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                      Organization
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-2">Create your account</h2>
                  <p className="text-xs sm:text-sm text-neutral-500">
                    {currentStep === 1 && 'Tell us about your organization'}
                    {currentStep === 2 && 'Enter your contact information'}
                    {currentStep === 3 && 'Choose the plan that fits your needs'}
                  </p>
                </div>

                {/* Labeled Step Indicator */}
                <LabeledStepper steps={CLIENT_STEPS} current={currentStep} />

                {/* Registration Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Step 1: Organization Details */}
                  {currentStep === 1 && (
                    <div className="space-y-4">
                      {/* Organization Name */}
                      <div>
                        <label htmlFor="organizationName" className="block text-sm font-medium text-neutral-700 mb-2">
                          Organization Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <input
                            type="text"
                            id="organizationName"
                            name="organizationName"
                            value={formData.organizationName}
                            onChange={handleChange}
                            placeholder="ABC Corporation"
                            className={inputClass}
                          />
                          <FieldTip text="Your company, team or institution that runs the training — trainers and sessions live under it." />
                        </div>
                        {errors.organizationName ? (
                          <p className="text-sm text-red-600 mt-1">{errors.organizationName}</p>
                        ) : (
                          <FieldInlineHelp text="Your company, team or institution that runs the training — trainers and sessions live under it." />
                        )}
                      </div>

                      {/* Website */}
                      <div>
                        <label htmlFor="website" className="block text-sm font-medium text-neutral-700 mb-2">
                          Website <span className="text-neutral-400 font-normal">(optional)</span>
                        </label>
                        <div className="relative group">
                          <input
                            type="url"
                            id="website"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            placeholder="https://www.example.com"
                            className={inputClass}
                          />
                          <FieldTip text="Optional — helps us verify your organization and personalize your workspace. Leave blank if you don't have one." />
                        </div>
                        <FieldInlineHelp text="Optional — helps us verify your organization. Leave blank if you don't have one." />
                      </div>

                      {/* Address Section */}
                      <div className="pt-2">
                        <p className="text-sm font-medium text-neutral-700 mb-1">Address <span className="text-neutral-400 font-normal">(optional)</span></p>
                        <p className="text-xs text-neutral-500 mb-3">Used on invoices and reports. You can add this later.</p>

                        {/* Street */}
                        <div className="mb-3">
                          <input
                            type="text"
                            id="street"
                            name="street"
                            value={formData.street}
                            onChange={handleChange}
                            placeholder="Street Address"
                            className={inputClass}
                          />
                        </div>

                        {/* City & State Row */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="City"
                            className={inputClass}
                          />
                          <input
                            type="text"
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            placeholder="State/Province"
                            className={inputClass}
                          />
                        </div>

                        {/* ZIP & Country Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            id="zipCode"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleChange}
                            placeholder="ZIP/Postal Code"
                            className={inputClass}
                          />
                          <input
                            type="text"
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder="Country"
                            className={inputClass}
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-2">
                          Description <span className="text-neutral-400 font-normal">(optional)</span>
                        </label>
                        <div className="relative group">
                          <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Brief description of your organization"
                            rows={2}
                            className={`${inputClass} resize-none`}
                          />
                          <FieldTip text="Optional — a short summary shown to your team and on reports." />
                        </div>
                        <FieldInlineHelp text="Optional — a short summary shown to your team and on reports." />
                      </div>

                      {/* Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="flex-1 px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
                        >
                          Back
                        </button>
                        <Button
                          type="button"
                          variant="primary"
                          size="lg"
                          onClick={handleNext}
                          className="flex-1"
                        >
                          <span className="flex items-center justify-center gap-2">
                            Continue
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Client Details */}
                  {currentStep === 2 && (
                    <div className="space-y-4">
                      {/* Name Fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative group">
                            <input
                              type="text"
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              placeholder="John"
                              className={inputClass}
                            />
                            <FieldTip text="Your name as a client." />
                          </div>
                          {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>}
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative group">
                            <input
                              type="text"
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              placeholder="Doe"
                              className={inputClass}
                            />
                            <FieldTip text="Your surname as a client." />
                          </div>
                          {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>}
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                          Work Email <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="name@company.com"
                            className={`${inputClass} pr-10`}
                          />
                          {formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2">
                              {emailCheck.checking && <Loader2 size={18} className="text-neutral-400 animate-spin" />}
                              {!emailCheck.checking && emailCheck.available === true && <CheckCircle2 size={18} className="text-green-500" />}
                              {!emailCheck.checking && emailCheck.available === false && <XCircle size={18} className="text-red-500" />}
                            </span>
                          )}
                          <FieldTip text="You'll use this email to sign in and receive reports." />
                        </div>
                        {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
                        {!errors.email && emailCheck.error && <p className="text-sm text-red-600 mt-1">{emailCheck.error}</p>}
                        {!errors.email && !emailCheck.error && emailCheck.available === true && (
                          <p className="text-sm text-green-600 mt-1">Email is available</p>
                        )}
                        {!errors.email && !emailCheck.error && emailCheck.available !== true && (
                          <FieldInlineHelp text="You'll use this email to sign in and receive reports." />
                        )}
                      </div>

                      {/* Phone */}
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-2">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 98765 43210"
                            className={inputClass}
                          />
                          <FieldTip text="We may use this for account security and important updates." />
                        </div>
                        {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
                        {!errors.phone && <FieldInlineHelp text="We may use this for account security and important updates." />}
                      </div>

                      {/* Job Title */}
                      <div>
                        <label htmlFor="jobTitle" className="block text-sm font-medium text-neutral-700 mb-2">
                          Job Title <span className="text-neutral-400 font-normal">(optional)</span>
                        </label>
                        <div className="relative group">
                          <input
                            type="text"
                            id="jobTitle"
                            name="jobTitle"
                            value={formData.jobTitle}
                            onChange={handleChange}
                            placeholder="Training Manager"
                            className={inputClass}
                          />
                          <FieldTip text="Optional — shown on the reports you generate." />
                        </div>
                        <FieldInlineHelp text="Optional — shown on the reports you generate." />
                      </div>

                      {/* Password */}
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Create a password"
                            className={`${inputClass} pr-11`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                          <FieldTip text="At least 8 characters. Mix letters and numbers for a stronger password." />
                        </div>
                        {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password}</p>}
                        {!errors.password && <p className="text-xs text-neutral-500 mt-1">Must be at least 8 characters</p>}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm your password"
                            className={`${inputClass} pr-11`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                          <FieldTip text="Re-type your password exactly to confirm it." />
                        </div>
                        {errors.confirmPassword && <p className="text-sm text-red-600 mt-1">{errors.confirmPassword}</p>}
                        {!errors.confirmPassword && <FieldInlineHelp text="Re-type your password exactly to confirm it." />}
                      </div>

                      {/* Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="flex-1 px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
                        >
                          Back
                        </button>
                        <Button
                          type="button"
                          variant="primary"
                          size="lg"
                          onClick={handleNext}
                          className="flex-1"
                        >
                          <span className="flex items-center justify-center gap-2">
                            Continue
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Plan Selection (Client - all plans) */}
                  {currentStep === 3 && (
                    <div className="space-y-4">
                      <PlanLimitsExplainer includeTrainerSeats={true} />

                      {/* Plan Cards */}
                      <div className="space-y-3">
                        <PlanCard
                          planKey="free"
                          name="Free Trial"
                          priceInr="₹0"
                          priceUsd="$0"
                          period="forever"
                          description="Experience Aktivity in a real training session"
                          features={['3 sessions total (one-time)', 'Up to 30 participants/session', 'All core features', 'Basic report']}
                        />
                        <PlanCard
                          planKey="pro"
                          name="Pro"
                          priceInr="₹1,999"
                          priceUsd="$24"
                          period="/month"
                          description="For trainers who want proof of impact"
                          features={['20 sessions/month', '30 participants/session', '1 trainer access', 'Full reports']}
                        />
                        <PlanCard
                          planKey="pro_plus"
                          name="Pro Plus"
                          priceInr="₹5,999"
                          priceUsd="$72"
                          period="/month"
                          description="Learning intelligence at scale"
                          features={['Unlimited sessions', '100 participants/session', 'Up to 3 trainer accounts', 'Advanced reports', 'Priority support']}
                        />
                        <PlanCard
                          planKey="enterprise_starter"
                          name="Enterprise Starter"
                          priceInr="₹1,80,000"
                          priceUsd="$2,160"
                          period="/year"
                          badge="Best Value"
                          description="For L&D Teams & Training Companies"
                          features={['200+ participants/session', 'Up to 5 trainer accounts', 'Unlimited sessions', 'Live dashboards', 'Trainer leaderboards', 'Program-level learning intelligence']}
                        />

                        {/* Enterprise Scale Note */}
                        <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-neutral-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <div>
                              <h4 className="font-semibold text-neutral-900 text-sm">Need Enterprise Scale?</h4>
                              <p className="text-xs text-neutral-600 mt-1">
                                Custom pricing - Talk to Sales. For large organizations with custom requirements.
                              </p>
                              <a href="mailto:sales@aktivity.io" className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-1 inline-block">
                                Contact Sales →
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reassurance */}
                      <p className="text-center text-xs text-neutral-500">
                        {formData.plan === 'free'
                          ? 'Free needs no credit card — upgrade anytime as your team grows.'
                          : 'No commitment — you can change or cancel your plan anytime.'}
                      </p>

                      {/* Payment Info for Paid Plans */}
                      {formData.plan !== 'free' && (
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm text-blue-800">
                                After registration, you'll be redirected to our secure payment partner to complete your subscription.
                              </p>
                              <p className="mt-1 text-xs font-medium text-blue-700">
                                {formData.plan === 'enterprise_starter' ? 'Billed yearly' : 'Billed monthly'} · GST applies · cancel anytime
                              </p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-blue-700">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                <span>Secure payment powered by Dodo Payments</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Terms */}
                      <p className="text-xs text-neutral-500">
                        By signing up, you agree to our{' '}
                        <a href="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                          Privacy Policy
                        </a>
                      </p>

                      {/* Error Message */}
                      {errors.submit && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                          {errors.submit}
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="flex-1 px-4 py-3 border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
                        >
                          Back
                        </button>
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          loading={loading}
                          className="flex-1"
                        >
                          <span className="flex items-center justify-center gap-2 text-sm sm:text-base">
                            {loading ? 'Creating Account...' : formData.plan === 'free' ? 'Complete Sign Up' : (
                              <>
                                <span className="sm:hidden">Pay Now</span>
                                <span className="hidden sm:inline">Continue to Payment</span>
                              </>
                            )}
                            {!loading && (
                              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={formData.plan === 'free' ? "M5 13l4 4L19 7" : "M17 8l4 4m0 0l-4 4m4-4H3"} />
                              </svg>
                            )}
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
